import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopicGameRenderer from '../components/topic/TopicGameRenderer';
import { primeSpeech } from '../components/topic/_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const ADVANCE_DELAY_MS = 1500;

export const VOCAB_GAMES_CATALOG = [
  {
    code: 'vocab-gacha', tier: 1,
    name_vi: 'Cỗ Máy Gacha', icon: '🎰',
    desc: 'Khám phá từ mới qua máy quay',
    color: HT.yellow, colorDark: HT.yellowShadow,
  },
  {
    code: 'vocab-monster-sound', tier: 2,
    name_vi: 'Quái Vật Nghe', icon: '👹',
    desc: 'Nghe âm — tap đúng ảnh',
    color: HT.coral, colorDark: HT.coralDark,
  },
  {
    code: 'vocab-monster-def', tier: 3,
    name_vi: 'Quái Vật Đoán', icon: '🧠',
    desc: 'Đọc định nghĩa — tap đúng ảnh',
    color: HT.coralDark, colorDark: HT.coralPress,
  },
  {
    code: 'vocab-train-spell', tier: 3,
    name_vi: 'Đoàn Tàu Đánh Vần', icon: '🚂',
    desc: 'Kéo chữ cái lên toa tàu',
    color: HT.green, colorDark: HT.greenDark,
  },
  {
    code: 'vocab-train-cloze', tier: 4,
    name_vi: 'Đoàn Tàu Ngữ Cảnh', icon: '🚆',
    desc: 'Kéo toa-ảnh vào câu thiếu',
    color: HT.greenDark, colorDark: '#3F7A2B',
  },
  {
    code: 'vocab-defender-image', tier: 4,
    name_vi: 'Chiến Binh Không Gian', icon: '🚀',
    desc: 'Gõ từ trước khi thiên thạch rơi',
    color: HT.teal, colorDark: '#3F9F95',
  },
  {
    code: 'vocab-defender-def', tier: 5,
    name_vi: 'Giải Mã Mật Mã', icon: '👽',
    desc: 'Gõ từ chỉ từ định nghĩa',
    color: '#A06CD5', colorDark: '#6E3FAB',
  },
];

// RFC4122 v4 — backend AnswerItem.game_id requires a real UUID.
const uuid4 = () =>
  (window.crypto?.randomUUID)
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
      });

const apiBase = () => process.env.REACT_APP_API_URL || '';
const apiHeaders = () => {
  const t = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
};

export default function TopicGamePlayPage() {
  const navigate = useNavigate();
  const { topicId, gameType } = useParams();

  const game = useMemo(
    () => VOCAB_GAMES_CATALOG.find(g => g.code === gameType) || null,
    [gameType],
  );

  const [queue, setQueue] = useState([]); // [{tag_id, code, name, ...}]
  const [queueIdx, setQueueIdx] = useState(0);
  const [currentPayload, setCurrentPayload] = useState(null);
  const [currentTagInfo, setCurrentTagInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [advanceTimer, setAdvanceTimer] = useState(null);
  const [topicName, setTopicName] = useState('');
  const advanceRef = useRef(null);
  // Mastery recording: buffer answers, flush silently every few — and on exit —
  // so free-play here counts toward word status just like ladder sessions.
  const answerBufRef = useRef([]);
  const currentTierRef = useRef(1);
  const questionStartRef = useRef(Date.now());

  const flushAnswers = (keepalive = false) => {
    const batch = answerBufRef.current;
    if (!batch.length) return;
    answerBufRef.current = [];
    fetch(`${apiBase()}/client/topics/games/attempts`, {
      method: 'POST',
      headers: apiHeaders(),
      body: JSON.stringify({ parent_topic_id: topicId, answers: batch, duration_ms: 0 }),
      keepalive,
    }).catch(() => {});
  };

  // Flush whatever remains when the player leaves: SPA unmount AND hard
  // navigation / tab close (pagehide — React cleanup does not run on unload).
  useEffect(() => {
    const onHide = () => flushAnswers(true);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      flushAnswers(true);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load topic leaves ────────────────────────────────────────────
  useEffect(() => {
    let cancel = false;
    setBootstrapping(true);
    fetch(`${apiBase()}/client/library/topics/${topicId}/children`, { headers: apiHeaders() })
      .then(r => r.json())
      .then(d => {
        if (cancel) return;
        const arr = Array.isArray(d) ? d : [];
        // Shuffle so each session has different order
        const shuffled = [...arr].sort(() => Math.random() - 0.5);
        setQueue(shuffled);
        setBootstrapping(false);
      })
      .catch(e => {
        if (!cancel) {
          setError(String(e.message || e));
          setBootstrapping(false);
        }
      });
    // Topic name (for header) — minimal fetch
    fetch(`${apiBase()}/client/topics/parents/${topicId}`, { headers: apiHeaders() })
      .then(r => r.json())
      .then(d => { if (!cancel) setTopicName(d?.name || ''); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [topicId]);

  // ── Fetch variant for current queueIdx whenever it changes ───────
  const fetchVariantAt = async (idx) => {
    const tag = queue[idx];
    if (!tag) {
      setCurrentPayload(null);
      setCurrentTagInfo(null);
      return;
    }
    setLoading(true);
    setError('');
    setCurrentPayload(null);
    setCurrentTagInfo(tag);
    try {
      const r = await fetch(`${apiBase()}/game-gen/preview`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          tag_id: tag.tag_id,
          game_type_code: gameType,
          n_variants: 1,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'preview failed');
      const v = (d.variants || [])[0];
      if (v?.payload) {
        currentTierRef.current = d.tier || 1;
        questionStartRef.current = Date.now();
        setCurrentPayload(v.payload);
      } else {
        setError(`Tag ${tag.code || tag.tag_code} không có variant — bỏ qua.`);
        setTimeout(() => { advanceTo(idx + 1); }, 600);
      }
    } catch (e) {
      setError(`Lỗi tải game: ${e.message || e}`);
      setTimeout(() => { advanceTo(idx + 1); }, 800);
    } finally {
      setLoading(false);
    }
  };

  const advanceTo = (idx) => {
    clearAdvance();
    if (idx >= queue.length) {
      flushAnswers(); // session complete — persist the tail of the buffer
      setCurrentPayload(null);
      setCurrentTagInfo(null);
      setQueueIdx(idx);
      return;
    }
    setQueueIdx(idx);
    fetchVariantAt(idx);
  };

  const clearAdvance = () => {
    if (advanceRef.current) {
      clearTimeout(advanceRef.current);
      advanceRef.current = null;
    }
    setAdvanceTimer(null);
  };

  // Start session once queue is loaded
  const sessionStarted = useRef(false);
  useEffect(() => {
    if (!sessionStarted.current && queue.length > 0) {
      sessionStarted.current = true;
      primeSpeech();
      advanceTo(0);
    }
    // eslint-disable-next-line
  }, [queue]);

  const handleAnswer = (ans) => {
    setStats(s => ({
      correct: s.correct + (ans.is_correct ? 1 : 0),
      total: s.total + 1,
    }));
    // Record for mastery — synthetic game_id is fine (grading keys off leaf+tier).
    if (currentTagInfo?.tag_id) {
      answerBufRef.current.push({
        game_id: uuid4(),
        leaf_tag_id: currentTagInfo.tag_id,
        tier: currentTierRef.current,
        is_correct: !!ans.is_correct,
        time_ms: Date.now() - questionStartRef.current,
      });
      if (answerBufRef.current.length >= 6) flushAnswers();
    }
    clearAdvance();
    setAdvanceTimer(ADVANCE_DELAY_MS);
    advanceRef.current = setTimeout(() => advanceTo(queueIdx + 1), ADVANCE_DELAY_MS);
  };

  const skipCurrent = () => {
    clearAdvance();
    advanceTo(queueIdx + 1);
  };

  const restart = () => {
    setStats({ correct: 0, total: 0 });
    setQueue(q => [...q].sort(() => Math.random() - 0.5));
    sessionStarted.current = false;
  };

  const goBack = () => {
    clearAdvance();
    navigate(`/vocab/words?topic=${topicId}`);
  };

  const isEnd = !currentPayload && !loading && !bootstrapping && stats.total > 0 && queueIdx >= queue.length;
  const accuracy = stats.total > 0 ? Math.round(100 * stats.correct / stats.total) : 0;

  if (!game) {
    return (
      <div style={{ padding: 40, fontFamily: FONT_BODY, textAlign: 'center' }}>
        <div style={{ fontSize: 20, color: HT.coralDark, fontWeight: 800 }}>Game type không tồn tại</div>
        <button onClick={goBack} style={btnPrimary}>← Quay lại</button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${HT.yellowBg} 0%, ${HT.cream} 100%)`,
      fontFamily: FONT_BODY, color: HT.text,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ─── Minimal top bar ─────────────────────────────────────── */}
      <header style={{
        background: HT.white,
        borderBottom: `1px solid ${HT.border}`,
        padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        boxShadow: '0 2px 6px rgba(139,111,71,0.06)',
        zIndex: 5,
      }}>
        <button
          onClick={goBack}
          style={{
            padding: '8px 16px', borderRadius: 999,
            border: `2px solid ${HT.border}`, background: HT.white,
            color: HT.textSoft, fontFamily: FONT_BODY,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >← Thoát</button>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 999,
          background: game.color, color: HT.white,
          fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 700,
          boxShadow: `0 3px 0 ${game.colorDark}`,
        }}>
          <span style={{ fontSize: 18 }}>{game.icon}</span>
          {game.name_vi}
        </div>
        {topicName && (
          <div style={{ fontSize: 13, color: HT.textSoft, fontWeight: 600 }}>
            · {topicName}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Session stats */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 14,
          fontSize: 13, fontWeight: 700, color: HT.text,
        }}>
          <span style={{ color: HT.textSoft }}>
            Từ <b style={{ color: HT.text }}>{Math.min(queueIdx + 1, queue.length)}</b> / {queue.length}
          </span>
          <span style={{ color: HT.textMuted }}>•</span>
          <span style={{ color: HT.textSoft }}>
            Đúng <b style={{ color: HT.greenDark }}>{stats.correct}</b> / {stats.total}
          </span>
          {advanceTimer ? (
            <span style={{ color: HT.coralDark, fontSize: 12 }}>⏳ Sang từ kế…</span>
          ) : null}
        </div>

        {!isEnd && currentPayload && (
          <button onClick={skipCurrent} style={{
            padding: '6px 14px', borderRadius: 999,
            border: 'none', background: HT.yellowSoft, color: HT.brownDark,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 2px 0 ${HT.yellow}`,
          }}>⏭ Bỏ qua</button>
        )}
      </header>

      {/* ─── Game stage ───────────────────────────────────────────── */}
      <main style={{
        flex: 1,
        padding: '20px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}>
        <div style={{ width: '100%', maxWidth: 1100 }}>
          {error && (
            <div style={{
              marginBottom: 12, padding: '10px 14px',
              background: HT.pinkBg, color: HT.coralDark,
              border: `1px solid ${HT.coral}`, borderRadius: 12,
              fontSize: 13, fontWeight: 700, textAlign: 'center',
            }}>⚠ {error}</div>
          )}

          {bootstrapping ? (
            <Loader text="Đang tải chủ đề..." />
          ) : queue.length === 0 ? (
            <EmptyState onBack={goBack} />
          ) : isEnd ? (
            <EndScreen stats={stats} accuracy={accuracy} onRestart={restart} onBack={goBack} game={game} />
          ) : currentPayload ? (
            <TopicGameRenderer
              key={`${gameType}-${currentPayload.tag_id || currentPayload.tag_code || queueIdx}`}
              payload={currentPayload}
              onAnswer={handleAnswer}
            />
          ) : (
            <Loader text={loading ? 'Đang tạo variant…' : 'Sẵn sàng...'} />
          )}
        </div>
      </main>
    </div>
  );
}

function Loader({ text }) {
  return (
    <div style={{
      padding: 60, textAlign: 'center',
      color: HT.textMuted, fontSize: 14, fontWeight: 700,
      fontFamily: FONT_BODY,
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
      {text}
    </div>
  );
}

function EmptyState({ onBack }) {
  return (
    <div style={{
      padding: 40, textAlign: 'center',
      background: HT.white,
      border: `2px dashed ${HT.border}`, borderRadius: 16,
    }}>
      <div style={{ fontSize: 48, marginBottom: 8 }}>📭</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: HT.text, marginBottom: 6 }}>
        Chủ đề trống
      </div>
      <div style={{ fontSize: 14, color: HT.textSoft, marginBottom: 18 }}>
        Chủ đề này chưa có từ vựng nào để chơi.
      </div>
      <button onClick={onBack} style={btnPrimary}>← Quay lại chủ đề</button>
    </div>
  );
}

function EndScreen({ stats, accuracy, onRestart, onBack, game }) {
  const mood = accuracy >= 80 ? 'great' : accuracy >= 50 ? 'good' : 'try';
  const moodInfo = {
    great: { emoji: '🏆', title: 'Tuyệt vời!', color: HT.greenDark, bg: HT.greenBg },
    good:  { emoji: '👍', title: 'Khá tốt!',  color: HT.coralDark, bg: HT.yellowSoft },
    try:   { emoji: '💪', title: 'Cố gắng nữa nhé!', color: HT.coralDark, bg: HT.pinkBg },
  }[mood];
  return (
    <div style={{
      padding: 36, textAlign: 'center',
      background: moodInfo.bg,
      border: `2px solid ${moodInfo.color}`,
      borderRadius: 24,
      maxWidth: 540, margin: '20px auto',
      boxShadow: '0 6px 24px rgba(139,111,71,0.10)',
    }}>
      <div style={{ fontSize: 72, marginBottom: 8 }}>{moodInfo.emoji}</div>
      <div style={{
        fontSize: 28, fontWeight: 800,
        fontFamily: FONT_DISPLAY, color: moodInfo.color,
        marginBottom: 12,
      }}>{moodInfo.title}</div>
      <div style={{ fontSize: 15, color: HT.text, marginBottom: 6 }}>
        Đã chơi <b>{stats.total}</b> từ với game <b>{game.icon} {game.name_vi}</b>
      </div>
      <div style={{ fontSize: 14, color: HT.textSoft, marginBottom: 24 }}>
        Đúng <b style={{ color: HT.greenDark, fontSize: 20 }}>{stats.correct}</b> / {stats.total}
        {' '}<span style={{ color: moodInfo.color }}>({accuracy}%)</span>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onRestart} style={btnPrimary}>▶ Chơi lại</button>
        <button onClick={onBack} style={btnSecondary}>← Về chủ đề</button>
      </div>
    </div>
  );
}

const btnPrimary = {
  padding: '14px 28px', borderRadius: 999, border: 'none',
  background: HT.coralDark, color: HT.white,
  fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 700,
  cursor: 'pointer',
  boxShadow: `0 4px 0 ${HT.coralPress}`,
};
const btnSecondary = {
  padding: '14px 24px', borderRadius: 999,
  border: `2px solid ${HT.border}`, background: HT.white,
  color: HT.textSoft, fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 700,
  cursor: 'pointer',
};
